/**
 * Run list of the Test Runs screen (epic 031 task 06): the newest 50 runs of
 * the selected campaign — created, mode, test email (copyable), status pill,
 * outcome badge, requested by. Rows newest first; a click opens the run
 * detail. The list is an audit trail — runs are never deleted (README
 * decision 16).
 */

import type { TestRunStatus, TestRunSummary } from '@leads-platform/shared';
import { CopyButton } from '../../components/ui/copy-button';
import { StatusPill } from '../../components/ui/status-pill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tag } from '../../components/ui/tag';
import { fmtDate } from '../../lib/format';

/** Local HH:mm:ss — the time half of a created stamp. */
function localTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toTimeString().slice(0, 8);
}

const MODE_LABELS: Record<TestRunSummary['mode'], string> = {
  admin: 'Admin',
  cto_link: 'CTO link',
  page: 'Test page',
};

/** Outcome badge — passed green / failed red. Null while in flight. */
function OutcomeBadge({ outcome }: { outcome: TestRunSummary['outcome'] }) {
  if (!outcome) return <span className="text-faint">—</span>;
  return (
    <Tag tone={outcome === 'passed' ? 'accent' : 'rose'}>
      {outcome === 'passed' ? 'Passed' : 'Failed'}
    </Tag>
  );
}

/** Run status — the shared enum straight into the pill (unknown values stay muted). */
function RunStatusPill({ status }: { status: TestRunStatus }) {
  return <StatusPill value={status} />;
}

interface RunListProps {
  runs: TestRunSummary[];
  selectedRunId: string | null;
  onSelect: (runId: string) => void;
}

export function RunList({ runs, selectedRunId, onSelect }: RunListProps) {
  const ordered = [...runs].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Created</TableHead>
          <TableHead>Mode</TableHead>
          <TableHead>Test email</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Outcome</TableHead>
          <TableHead>Requested by</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {ordered.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
              No test runs yet — create one above.
            </TableCell>
          </TableRow>
        ) : (
          ordered.map((run) => {
            const selected = run.id === selectedRunId;
            return (
              <TableRow
                key={run.id}
                onClick={() => onSelect(run.id)}
                className={'cursor-pointer ' + (selected ? 'bg-accent/10' : 'hover:bg-line/30')}
              >
                <TableCell className="whitespace-nowrap">
                  <span className="font-mono text-xs text-ink">{fmtDate(run.createdAt)}</span>
                  <span className="block font-mono text-xs text-faint">
                    {localTime(run.createdAt)}
                  </span>
                </TableCell>
                <TableCell className="text-muted">{MODE_LABELS[run.mode]}</TableCell>
                <TableCell>
                  <span className="mr-2 break-all font-mono text-xs">{run.testEmail}</span>
                  <CopyButton value={run.testEmail} label="Copy" title="Copy the test address" />
                </TableCell>
                <TableCell>
                  <RunStatusPill status={run.status} />
                </TableCell>
                <TableCell>
                  <OutcomeBadge outcome={run.outcome} />
                </TableCell>
                <TableCell className="text-muted">{run.requestedBy}</TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
